// Sticky header for the Super Admin shell. Mirrors AppHeader's pattern
// but trimmed to the Super-side surface: route-derived title, Cmd+K
// cross-tenant search, dark-mode toggle, profile dropdown with sign-out.
//
// Notifications bell is intentionally NOT included yet — there's no
// super-admin notifications stream wired on the backend. Easy to add
// later by reusing the same component as AppHeader's `NotificationBell`.
import { useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  Bell,
  ChevronDown,
  ChevronsUpDown,
  LogOut,
  Search,
  User as UserIcon,
} from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/stores/auth"
import { useTheme } from "@/contexts/ThemeContext"
import { useKeyboardShortcuts } from "@/hooks/useKeyboardShortcuts"
import { SuperGlobalSearch } from "@/components/layout/SuperGlobalSearch"
import { api, logout } from "@/lib/api"
import { cn } from "@/lib/utils"

function initials(input: string | undefined): string {
  if (!input) return "SA"
  const parts = input.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export function SuperAdminHeader() {
  const navigate = useNavigate()
  const { isDark, toggle } = useTheme()
  const user = useAuthStore((s) => s.user)
  const [searchOpen, setSearchOpen] = useState(false)
  // Action-center total powers the bell badge. Polled on mount + every 60s.
  const [actionCount, setActionCount] = useState(0)
  const isMac = useRef(
    typeof navigator !== "undefined" && /mac/i.test(navigator.platform),
  ).current

  useEffect(() => {
    let cancelled = false
    const fetchCount = () => {
      api
        .get<{ data: { total: number } }>("/super/action-center/count")
        .then((r) => {
          if (!cancelled) setActionCount(r.data.data?.total ?? 0)
        })
        .catch(() => {
          /* non-fatal */
        })
    }
    fetchCount()
    const id = window.setInterval(fetchCount, 60_000)
    return () => {
      cancelled = true
      window.clearInterval(id)
    }
  }, [])

  // Same shortcut hook the client-side AppHeader uses — Cmd+K / Ctrl+K
  // opens the search modal.
  useKeyboardShortcuts({
    onSearch: () => setSearchOpen(true),
  })

  async function signOut() {
    await logout()
    navigate("/super/login", { replace: true })
  }

  return (
    <header className="sticky top-0 z-40 flex h-14 items-center gap-4 border-b border-[var(--color-border)] bg-[var(--color-bg)] px-6">
      {/* Left: empty spacer. Each page renders its own PageHeader title,
          so the header bar must NOT duplicate it. */}
      <div className="min-w-0 flex-1" />

      {/* Center: search trigger (desktop) */}
      <button
        onClick={() => setSearchOpen(true)}
        className="hidden h-9 w-[320px] items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-subtle)] px-3 text-sm text-[color:var(--color-text-muted)] hover:border-[var(--color-border-strong)] md:flex"
      >
        <Search className="h-4 w-4" />
        <span className="flex-1 text-left">Search clients, tickets, invoices…</span>
        <kbd className="rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 py-0.5 text-[10px]">
          {isMac ? "⌘" : "Ctrl"}K
        </kbd>
      </button>

      {/* Right: search trigger (mobile) + profile dropdown */}
      <div className="flex flex-1 items-center justify-end gap-1">
        <button
          onClick={() => setSearchOpen(true)}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] md:hidden"
          aria-label="Search"
        >
          <Search className="h-5 w-5" strokeWidth={1.6} />
        </button>

        {/* Notifications bell — surfaces the action-center total (open
            tickets + overdue invoices + new clients). Red badge when >0;
            dropdown links to the Action Center page. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="relative flex h-9 w-9 items-center justify-center rounded-lg text-[color:var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" strokeWidth={1.6} />
              {actionCount > 0 ? (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-500 px-1 text-[9px] font-bold text-white">
                  {actionCount > 9 ? "9+" : actionCount}
                </span>
              ) : null}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="py-2">
              <span className="block text-sm font-semibold text-foreground">
                Notifications
              </span>
              <span className="block text-xs font-normal text-[color:var(--color-text-muted)]">
                {actionCount > 0
                  ? `${actionCount} item${actionCount === 1 ? "" : "s"} need attention`
                  : "All caught up — nothing needs attention."}
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/super/action-center")}>
              <Bell className="mr-2 h-4 w-4" /> View Action Center
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="ml-1 flex items-center gap-2 rounded-lg px-1.5 py-1 hover:bg-[var(--color-bg-subtle)]">
              <span
                className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {initials(user?.fullName ?? user?.email)}
              </span>
              <span className="hidden max-w-[140px] truncate text-sm font-medium text-foreground lg:block">
                {user?.fullName ?? user?.email ?? "Super Admin"}
              </span>
              <ChevronDown className="hidden h-4 w-4 text-[color:var(--color-text-muted)] lg:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel className="flex items-center gap-3 py-2">
              <span
                className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-semibold"
                style={{
                  background: "var(--color-accent-light)",
                  color: "var(--color-accent-hover)",
                }}
              >
                {initials(user?.fullName ?? user?.email)}
              </span>
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-foreground">
                  {user?.fullName ?? "Super Admin"}
                </span>
                <span className="block truncate text-xs font-normal text-[color:var(--color-text-muted)]">
                  {user?.email}
                </span>
              </span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/super/profile")}>
              <UserIcon className="mr-2 h-4 w-4" /> My Profile
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              // Same prevent-default trick as the client header: keep the
              // menu open after toggling so the user can see the switch
              // animate before they close it themselves.
              onSelect={(e) => {
                e.preventDefault()
                toggle()
              }}
            >
              <span className="flex flex-1 items-center">
                <ChevronsUpDown className="mr-2 h-4 w-4" /> Dark Mode
              </span>
              <span
                className={cn(
                  "ml-2 inline-flex h-4 w-8 items-center rounded-full px-0.5 transition-colors",
                  isDark
                    ? "bg-[var(--color-accent)]"
                    : "bg-[var(--color-border-strong)]",
                )}
              >
                <span
                  className={cn(
                    "h-3 w-3 rounded-full bg-white transition-transform",
                    isDark && "translate-x-4",
                  )}
                />
              </span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={signOut}
              className="text-[#b91c1c] focus:text-[#b91c1c]"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <SuperGlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  )
}
